import React, { useRef } from "react"

import styled from "styled-components"

import { spacing, IconVeryHappyLarge } from "@izettle/otto"
import { useLocalization } from "@izettle/tenko-react"

import { useModal } from "@/core/components/modal"
import {
  Container,
  Content,
  Row,
  Flex,
  Logo,
  SvgIcon,
  Button,
  AnchorButton,
  Title1,
  Footer,
  SmallSecondaryText,
  RegularText,
  RegularSecondaryText,
  Anchor,
} from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { PaypalPrivacyStatementLink } from "@/core/utils/constant"
import {
  PrivacyPolicyFrom,
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"

import { Modals } from "./types"

export type WelcomeProps = {
  orgName: string
  purchaseUuid: string
  orgPrivacyPolicyLink?: string
  orgLogoUrl?: string
}

const OrgTitle = styled(RegularText)`
  margin: 0 0 0 ${spacing.small};
`

const CenteredDiv = styled.div`
  text-align: center;
`

const MainFlex = styled(Flex)`
  white-space: break-spaces;
`

const FlexItem = styled(Flex)`
  margin-bottom: ${spacing.medium};
`

export const Welcome = ({
  orgName,
  orgLogoUrl,
  purchaseUuid,
  orgPrivacyPolicyLink,
}: WelcomeProps) => {
  const ref = useRef(null)
  const { text } = useLocalization()
  const { goNext } = useSlider()
  const [showPolicyModal] = useModal(Modals.Policy)
  const { track } = useSignUpTracking(purchaseUuid)

  const handlePolicyClicked = () => {
    showPolicyModal(ref)
    track(TrackingAction.ViewedPrivacyPolicy, {
      page: PrivacyPolicyFrom.OptOutPage,
      sellerHasPrivacyPolicy: !!orgPrivacyPolicyLink,
    })
  }

  return (
    <Container withNoHeaderSpace>
      <Content vCentered>
        <Row>
          <Flex vCentered hCentered>
            <Flex aria-hidden>
              {orgLogoUrl ? (
                <Logo src={orgLogoUrl} />
              ) : (
                <SvgIcon html={IconVeryHappyLarge.svg} />
              )}
            </Flex>
            <OrgTitle>{orgName}</OrgTitle>
          </Flex>
          <CenteredDiv>
            <Title1>{text("SIGNUP_STAY_IN_THE_KNOW")}</Title1>
            <RegularSecondaryText>
              {text("SIGNUP_LANDING_SUBTITLE", [orgName])}
            </RegularSecondaryText>
          </CenteredDiv>
          <MainFlex direction="column" ref={ref}>
            <SmallSecondaryText>
              {text("SIGNUP_WELCOME_LEGAL_DISCLAIMER_2", [orgName])}
            </SmallSecondaryText>
            <FlexItem>
              <Anchor href={PaypalPrivacyStatementLink} target="_blank" size="small">
                {text("SIGNUP_PRIVACY_POLICY_PAYPAL_PRIVACY_STATEMENT")}
              </Anchor>
            </FlexItem>
            <FlexItem>
              {orgPrivacyPolicyLink ? (
                <Anchor href={orgPrivacyPolicyLink} target="_blank" size="small">
                  {text("SIGNUP_MERCHANT_PRIVACY_POLICY", [orgName])}
                </Anchor>
              ) : (
                <AnchorButton
                  data-testid="click-to-know-more"
                  className="small"
                  size="small"
                  onClick={handlePolicyClicked}
                >
                  {text("SIGNUP_LEARN_HOW_YOUR_INFORMATION_IS_HANDLED")}
                </AnchorButton>
              )}
            </FlexItem>
          </MainFlex>
        </Row>
      </Content>
      <Footer>
        <Button size="large" className="focus" onClick={goNext}>
          {text("SIGNUP_SIGN_UP")}
        </Button>
      </Footer>
    </Container>
  )
}
